import { useEffect, useState } from 'react';
import happy from '../../happy.svg';
import {
  Column,
  Font,
  PrimaryLinkButton,
  ThinBorderStyle,
} from './DesignSystem';
import {
  buildPreviewLinks,
  readPreviewConversationID,
} from '@/services/PreviewLinks';

const AutoRedirectDelayMS = 1500;

const PageStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: 'calc(env(safe-area-inset-top, 0px) + 40px) 24px 32px',
  background:
    'linear-gradient(180deg, rgb(246, 250, 255) 0%, rgb(232, 241, 252) 100%)',
};

const ContentStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 440,
  gap: 14,
  alignItems: 'stretch',
  marginInline: 'auto',
};

const CenteredContentStyle: React.CSSProperties = {
  alignItems: 'center',
  textAlign: 'center',
  gap: 8,
};

const HeadingStyle: React.CSSProperties = {
  margin: 0,
};

const CopyStyle: React.CSSProperties = {
  margin: 0,
};

const NoticeStyle: React.CSSProperties = {
  ...ThinBorderStyle,
  padding: 16,
  backgroundColor: 'rgba(255, 255, 255, 0.66)',
  gap: 8,
};

const HeroIconStyle: React.CSSProperties = {
  width: 88,
  height: 88,
  alignSelf: 'center',
};

const InlineLabelStyle: React.CSSProperties = {
  display: 'block',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'rgba(0, 0, 0, 0.82)',
  textShadow: 'none',
  opacity: 1,
};

const LogoBadgeStyle: React.CSSProperties = {
  ...ThinBorderStyle,
  alignSelf: 'center',
  padding: '12px 16px',
  backgroundColor: 'rgba(255, 255, 255, 1)',
  boxShadow: '0 14px 28px rgba(0, 0, 0, 0.08)',
};

export function PublicPreviewPage() {
  const conversationID = readPreviewConversationID(window.location.search);
  const [hasAttemptedAutoRedirect, setHasAttemptedAutoRedirect] =
    useState(false);
  const appDeepLink =
    conversationID === null
      ? null
      : buildPreviewLinks({ conversationID }).appDeepLink;

  useEffect(() => {
    if (appDeepLink === null) {
      return;
    }

    const redirectTimer = window.setTimeout(() => {
      setHasAttemptedAutoRedirect(true);
    }, AutoRedirectDelayMS);

    window.location.assign(appDeepLink);

    return () => {
      window.clearTimeout(redirectTimer);
    };
  }, [appDeepLink]);

  if (conversationID === null) {
    return (
      <main style={PageStyle}>
        <section style={{ width: '100%' }}>
          <Column style={ContentStyle}>
            <div style={LogoBadgeStyle}>
              <Font kind="font-landing-header">
                <span style={InlineLabelStyle}>Codify</span>
              </Font>
            </div>
            <Column style={CenteredContentStyle}>
              <img alt="" src={happy} style={HeroIconStyle} />
              <div role="heading" aria-level={1} style={HeadingStyle}>
                <Font kind="font-preview-header">Invalid preview link</Font>
              </div>
              <div style={CopyStyle}>
                <Font kind="font-preview-text">
                  This preview link is missing a valid conversation ID.
                </Font>
              </div>
            </Column>
            <Column style={NoticeStyle}>
              <Font kind="font-preview-text">
                Ask the sender to generate a new QR code or preview link from
                Codify.
              </Font>
            </Column>
          </Column>
        </section>
      </main>
    );
  }

  return (
    <main style={PageStyle}>
      <section style={{ width: '100%' }}>
        <Column style={ContentStyle}>
          <div style={LogoBadgeStyle}>
            <Font kind="font-landing-header">
              <span style={InlineLabelStyle}>Codify</span>
            </Font>
          </div>
          <Column style={CenteredContentStyle}>
            <img alt="" src={happy} style={HeroIconStyle} />
            <div role="heading" aria-level={1} style={HeadingStyle}>
              <Font kind="font-preview-header">Preview this app in Codify</Font>
            </div>
            <div style={CopyStyle}>
              <Font kind="font-preview-text">
                {hasAttemptedAutoRedirect
                  ? "We couldn't redirect you to the app automatically. But that's okay!"
                  : 'Trying to open the Codify mobile app automatically...'}
              </Font>
            </div>
            <div style={CopyStyle}>
              <Font kind="font-preview-text">
                If Codify is installed on this device, use the button below to
                open the native preview.
              </Font>
            </div>
            <PrimaryLinkButton
              href={appDeepLink}
              text="Open in app"
              style={{ minHeight: 48 }}
            />
          </Column>
          <Column style={NoticeStyle}>
            <Font kind="font-preview-text">
              If nothing happens, the app is not installed on this device yet.
            </Font>
          </Column>
        </Column>
      </section>
    </main>
  );
}
